import React, { memo, useCallback } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Icon, theme } from "uikit";

import { InternalNotification } from "../../../../../../../../services";
import { StyledP } from "../../../../../../../../components/common";

const BORDER = "#e0e0e0";

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 999;
`;

const Panel = styled.div`
	position: fixed;
	top: 0;
	right: 60px;
	bottom: 0;
	width: 380px;
	background: #fff;
	box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
	z-index: 1000;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const HeaderBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	border-bottom: 1px solid ${BORDER};
`;

const ListContainer = styled.div`
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	padding: 8px 0;
	min-height: 0;
`;

const FooterBar = styled.div`
	padding: 12px 20px;
	border-top: 1px solid ${BORDER};
	flex-shrink: 0;
`;

const ClearButton = styled.button`
	width: 100%;
	padding: 10px 16px;
	font-size: 14px;
	cursor: pointer;
	border: 1px solid ${BORDER};
	border-radius: 6px;
	background: #fff;
	color: ${theme.colors.secondary};
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
	&:not(:disabled):hover {
		background: #f5f5f5;
	}
`;

const NotificationItem = styled.div<{ $unread: boolean }>`
	padding: 12px 20px;
	border-bottom: 1px solid ${BORDER};
	background: ${({ $unread }) => ($unread ? "#f5f9ff" : "transparent")};
	cursor: default;
	overflow: hidden;
`;

const EmptyState = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	color: ${theme.colors.disabled_text};
	font-size: 14px;
`;

const TimeText = styled.span`
	font-size: 12px;
	color: ${theme.colors.disabled_text};
	flex-shrink: 0;
	white-space: nowrap;
`;

const TitleText = styled.span<{ size?: string; fw?: number }>`
	font-size: ${({ size }) => size || "14px"};
	font-weight: ${({ fw }) => fw || 400};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

const BodyText = styled.p`
	margin: 0;
	font-size: 14px;
	color: ${theme.colors.secondary};
	word-break: break-word;
	white-space: pre-wrap;
`;

const NotificationSidebar: React.FC<NotificationSidebar.Props> = memo(
	({
		onClose,
		notifications,
		onClearAll,
		clearing,
		sessionMarkedReadIds,
		onMarkedReadSession,
	}) => {
		const { t } = useTranslation();

		const markAsRead = useCallback((ids: number[]) => {
			if (!ids.length) return;
			Promise.all(
				ids.map(
					(id) =>
						InternalNotification.markRead(id) ?? Promise.resolve(),
				),
			).catch(() => {});
		}, []);

		const markedIdsRef = React.useRef<Set<number>>(new Set());

		React.useEffect(() => {
			const unreadIds = notifications
				.filter(
					(n) =>
						!n.readAt &&
						!sessionMarkedReadIds?.has(n.id) &&
						!markedIdsRef.current.has(n.id),
				)
				.map((n) => n.id);
			if (unreadIds.length) {
				unreadIds.forEach((id) => markedIdsRef.current.add(id));
				onMarkedReadSession?.(unreadIds);
				markAsRead(unreadIds);
			}
		}, [
			notifications,
			markAsRead,
			sessionMarkedReadIds,
			onMarkedReadSession,
		]);

		const formatTime = useCallback((dateStr: string | null) => {
			if (!dateStr) return "";
			const d = new Date(dateStr);
			const now = new Date();
			const isToday = d.toDateString() === now.toDateString();
			const time = d.toLocaleTimeString([], {
				hour: "2-digit",
				minute: "2-digit",
			});
			if (isToday) return time;
			return `${d.toLocaleDateString()} ${time}`;
		}, []);

		const resolveNotificationTitle = useCallback(
			(n: InternalNotification.Model) => {
				const i18n = n.payload?.i18n;
				if (i18n?.titleKey) {
					return t(i18n.titleKey, i18n.params ?? {});
				}
				return n.title || "";
			},
			[t],
		);

		const resolveNotificationText = useCallback(
			(n: InternalNotification.Model) => {
				const i18n = n.payload?.i18n;
				if (i18n?.textKey) {
					return t(i18n.textKey, i18n.params ?? {});
				}
				return n.text || "";
			},
			[t],
		);

		const handleClear = useCallback(() => {
			Promise.resolve(onClearAll()).catch(() => {});
		}, [onClearAll]);

		return (
			<>
				<Overlay onClick={onClose} />
				<Panel>
					<HeaderBar>
						<StyledP font={{ size: "16px", fw: 600 }}>
							{t("orderPage.rightMenu.notifications") ||
								"Сповіщення"}
						</StyledP>
						<button
							type="button"
							onClick={onClose}
							style={{
								all: "unset",
								cursor: "pointer",
								display: "flex",
							}}
						>
							<Icon
								id="close"
								size={20}
								colors={[theme.colors.secondary]}
							/>
						</button>
					</HeaderBar>
					<ListContainer>
						{notifications.length === 0 ? (
							<EmptyState>
								{t("orderPage.rightMenu.noNotifications") ||
									"Немає сповіщень"}
							</EmptyState>
						) : (
							notifications.map((notification) => (
								<NotificationItem
									key={notification.id}
									$unread={
										!notification.readAt &&
										!sessionMarkedReadIds?.has(
											notification.id,
										)
									}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											gap: 8,
											marginBottom: 4,
										}}
									>
										<TitleText size="13px" fw={600}>
											{resolveNotificationTitle(
												notification,
											)}
										</TitleText>
										<TimeText>
											{formatTime(notification.createdAt)}
										</TimeText>
									</div>
									<BodyText>
										{resolveNotificationText(notification)}
									</BodyText>
								</NotificationItem>
							))
						)}
					</ListContainer>
					{notifications.length > 0 && (
						<FooterBar>
							<ClearButton
								type="button"
								disabled={clearing}
								onClick={handleClear}
							>
								{t("orderPage.rightMenu.clearNotifications") ||
									"Очистити"}
							</ClearButton>
						</FooterBar>
					)}
				</Panel>
			</>
		);
	},
);

declare namespace NotificationSidebar {
	interface Props {
		onClose: () => void;
		notifications: InternalNotification.Model[];
		onClearAll: () => void | Promise<void>;
		clearing?: boolean;
		/** Treat as read in UI immediately (subscription may lag). */
		sessionMarkedReadIds?: ReadonlySet<number>;
		onMarkedReadSession?: (ids: number[]) => void;
	}
}

export default NotificationSidebar;
